import { useMemo, useState } from 'react';
import { Text } from '@mantine/core';
import { Document, Page, pdfjs } from 'react-pdf';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader.tsx';
import { PDF_WORKER_SRC } from '@dealstack/frontend/shared/utils/pdfWorker';
import type { SignatureSlotDisplay } from '@dealstack/frontend/shared/components/DocumentEditor/extensions/signatureNode';

import type {
	PreviewDateBoxRes,
	PreviewFieldBoxRes,
	PreviewSignatureBoxRes,
} from '@dealstack/frontend/shared/components/FieldBinding/fieldBinding.types.ts';
import { PreviewFieldOverlay } from './PreviewFieldOverlay.tsx';
import { PreviewStampOverlay } from './PreviewStampOverlay.tsx';
import { PreviewPage, PreviewScroll } from './docxFlowPreview.styled.ts';

pdfjs.GlobalWorkerOptions.workerSrc = PDF_WORKER_SRC;

interface Props {
	file: Blob | undefined;
	isLoading: boolean;
	/** Matched to the editor's page width so switching views does not resize the document. */
	pageWidthMm: number;
	/**
	 * Where this render's signature anchors landed, so a stamp can be drawn in the space it
	 * reserved for them — the file itself leaves those boxes empty.
	 */
	signatureBoxes: PreviewSignatureBoxRes[];
	dateBoxes: PreviewDateBoxRes[];
	/** Label + role per slot key, for the stamp drawn in each box. */
	slotDisplay: Record<string, SignatureSlotDisplay>;
	/** Whether stamps carry their metadata block, matching what generation will draw. */
	showStampMetadata: boolean;
	/** Where this render's field values landed, so each can be tinted where it sits. */
	fieldBoxes: PreviewFieldBoxRes[];
	/** The author's choice. Off leaves the page exactly as generation will produce it. */
	highlightFields: boolean;
}

/** A page's intrinsic size, in PDF points. */
interface PageSizePt {
	widthPt: number;
	heightPt: number;
}

const MM_PER_INCH = 25.4;
const CSS_DPI = 96;

/**
 * The rendered document, page by page.
 *
 * A plain `react-pdf` stack rather than `PdfViewer` or `FieldBindingCanvas`: the first brings
 * its own header, download and back button, which would nest inside the editor's own header, and
 * the second exists to draw and drag binding boxes onto a page. Neither is what a preview is —
 * this is a read-only look at what generation will produce.
 */
export const DocxFlowPreview = ({
	file,
	isLoading,
	pageWidthMm,
	signatureBoxes,
	dateBoxes,
	slotDisplay,
	showStampMetadata,
	fieldBoxes,
	highlightFields,
}: Props) => {
	const [pageCount, setPageCount] = useState(0);
	// Each page's own size, learned as it renders. A stamp cannot be placed without it: the
	// boxes are in PDF space, whose origin is the page's BOTTOM-left corner.
	const [pageSizes, setPageSizes] = useState<Record<number, PageSizePt>>({});
	const widthPx = Math.round((pageWidthMm / MM_PER_INCH) * CSS_DPI);

	const boxesByPage = useMemo(() => {
		const grouped = new Map<number, PreviewSignatureBoxRes[]>();
		for (const box of signatureBoxes) {
			grouped.set(box.pageNum, [...(grouped.get(box.pageNum) ?? []), box]);
		}
		return grouped;
	}, [signatureBoxes]);

	const dateBoxesByPage = useMemo(() => {
		const grouped = new Map<number, PreviewDateBoxRes[]>();
		for (const box of dateBoxes) {
			grouped.set(box.pageNum, [...(grouped.get(box.pageNum) ?? []), box]);
		}
		return grouped;
	}, [dateBoxes]);

	const fieldBoxesByPage = useMemo(() => {
		const grouped = new Map<number, PreviewFieldBoxRes[]>();
		if (!highlightFields) {
			return grouped;
		}
		for (const box of fieldBoxes) {
			grouped.set(box.pageNum, [...(grouped.get(box.pageNum) ?? []), box]);
		}
		return grouped;
	}, [fieldBoxes, highlightFields]);

	if (isLoading || file === undefined) {
		return (
			<PreviewScroll data-testid="docx-flow-preview-loading">
				<InlineLoader />
			</PreviewScroll>
		);
	}

	return (
		<PreviewScroll data-testid="docx-flow-preview">
			<Document
				file={file}
				onLoadSuccess={({ numPages }) => setPageCount(numPages)}
				loading={<InlineLoader />}
				error={
					<Text size="sm" c="orange.7">
						The rendered document could not be displayed.
					</Text>
				}
			>
				{Array.from({ length: pageCount }, (_unused, index) => {
					const pageNum = index + 1;
					const boxes = boxesByPage.get(pageNum) ?? [];
					const pageDateBoxes = dateBoxesByPage.get(pageNum) ?? [];
					const fields = fieldBoxesByPage.get(pageNum) ?? [];
					const size = pageSizes[pageNum];
					return (
						<PreviewPage key={index}>
							<Page
								pageNumber={pageNum}
								width={widthPx}
								renderTextLayer={false}
								renderAnnotationLayer={false}
								onRenderSuccess={(page) =>
									setPageSizes((known) =>
										known[pageNum]?.heightPt === page.originalHeight
											? known
											: {
													...known,
													[pageNum]: {
														widthPt: page.originalWidth,
														heightPt: page.originalHeight,
													},
												}
									)
								}
							/>
							{/* Under the stamps, so an anchor inside a highlighted cell still
							    reads as a stamp. */}
							{fields.length > 0 && size !== undefined && (
								<PreviewFieldOverlay
									boxes={fields}
									pageHeightPt={size.heightPt}
									scale={widthPx / size.widthPt}
								/>
							)}
							{/* Only once the page's size is known, and only where there is
							    something to draw — a document that anchors no signatures gets no
							    overlay at all. */}
							{(boxes.length > 0 || pageDateBoxes.length > 0) &&
								size !== undefined && (
									<PreviewStampOverlay
										boxes={boxes}
										dateBoxes={pageDateBoxes}
										pageHeightPt={size.heightPt}
										// The page is rendered to a stated width, so the pixels-per-
										// point factor is that width over the page's intrinsic one.
										scale={widthPx / size.widthPt}
										slotDisplay={slotDisplay}
										showMetadata={showStampMetadata}
									/>
								)}
						</PreviewPage>
					);
				})}
			</Document>
		</PreviewScroll>
	);
};
